import { Pencil, Upload } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui";
import type { FieldDef } from "@/types/content";

import type { FormValues } from "../types";
import { getFieldPresets, isPresent } from "../utils";
import { DynamicField } from "./DynamicField";
import { PresetButtons } from "./fields/PrimitiveFields";

interface Props {
  cluster: FieldDef[];
  /** The member whose placeholder references a `presets.<namespace>`. */
  hub: FieldDef;
  values: FormValues;
  setValues: (patch: FormValues) => void;
}

const PRESET_MODE = "__preset__";

const siblingButton = (field: FieldDef) =>
  field.type === "image"
    ? { label: "Upload", Icon: Upload }
    : { label: "Describe", Icon: Pencil };

export const PresetClusterField = ({ cluster, hub, values, setValues }: Props) => {
  const presets = getFieldPresets(hub);
  const siblings = cluster.filter((f) => f.id !== hub.id);

  const [mode, setMode] = useState<string>(
    () => siblings.find((s) => isPresent(values[s.id]))?.id ?? PRESET_MODE,
  );

  useEffect(() => {
    if (mode === PRESET_MODE) {
      const present = siblings.find((s) => isPresent(values[s.id]));
      if (present) setMode(present.id);
    } else if (!siblings.some((s) => s.id === mode)) {
      setMode(PRESET_MODE);
    }
  }, [mode, siblings, values]);

  const activeSibling = siblings.find((s) => s.id === mode) ?? null;
  const selectedPreset = mode === PRESET_MODE ? values[hub.id] : undefined;

  const selectPreset = (value: string) => {
    setMode(PRESET_MODE);
    const patch: FormValues = { [hub.id]: value };
    for (const s of siblings) patch[s.id] = undefined;
    setValues(patch);
  };

  const selectSibling = (sibling: FieldDef) => {
    setMode(sibling.id);
    const patch: FormValues = { [hub.id]: undefined };
    for (const s of siblings) {
      if (s.id !== sibling.id) patch[s.id] = undefined;
    }
    setValues(patch);
  };

  return (
    <div className="space-y-3">
      <label className="block font-medium text-sm text-zinc-700">{hub.label}</label>
      {hub.description && <p className="text-xs text-zinc-400">{hub.description}</p>}

      <div className="flex flex-wrap gap-2">
        <PresetButtons presets={presets} value={selectedPreset} onSelect={selectPreset} />
        {siblings.map((sibling) => {
          const { label, Icon } = siblingButton(sibling);
          return (
            <Button
              key={sibling.id}
              variant={mode === sibling.id ? "default" : "outline"}
              onClick={() => selectSibling(sibling)}
            >
              <Icon className="h-4 w-4" />
              {label}
            </Button>
          );
        })}
      </div>

      {activeSibling && (
        <DynamicField
          field={activeSibling}
          value={values[activeSibling.id]}
          onChange={(v) => setValues({ [activeSibling.id]: v })}
        />
      )}
    </div>
  );
};
